import { AnimcoType } from '@app-types/animco';
import { WallType } from '@app-types/config';
import { ContractType } from '@app-types/contract';
import { ProductChildFull, ProductParentFull } from '@app-types/product';

import { formatTagText, sendPageViewTracking, sendTracking } from '@helpers/tracking';

import { STOCK_STATUS } from '@constants/stock';
import { TAG_CATEGORIES } from '@constants/tracking';

import { CTASource } from '@product-detail/constants';

export function pageTrackingHandler(
    type: WallType,
    isRenewal: boolean,
    product: ProductParentFull,
    animco: AnimcoType,
    contract?: ContractType,
) {
    let pageCategory: string;
    let page: string;
    switch (type) {
        case WallType.PHONE:
        case WallType.SALES_PHONE:
            pageCategory = 'Fiche_produit_telephone';
            page = `Telephone_${product.title}`;
            break;
        case WallType.ACCESSORY:
        case WallType.SALES_ACCESSORY:
            pageCategory = 'Fiche_produit_accessoire';
            page = `Accessoire_${product.title}`;
            break;
        case WallType.HOTSPOT:
            pageCategory = 'Fiche_produit_hotspot_tablette';
            page = `Hotspot_Tablette_${product.title}`;
            break;
        case WallType.ODR:
            pageCategory = 'Fiche_produit_odr';
            page = `Odr_${product.title}`;
            break;
        case WallType.KIDS_WATCH:
            pageCategory = 'Fiche_produit_kids_watch';
            page = `Kids_Watch_${product.title}`;
            break;
    }

    return (selectedProduct: ProductChildFull, tradeInBonus = 0) => {
        sendPageViewTracking(isRenewal, {
            page,
            pageCategory,
            marketLine: 'Mobile',
            isB2B: false,
            eCommerceStep: 'Fiche_produit',
            variant: {
                repriseMobile: `${tradeInBonus > 0 ? 'avec' : 'sans'}_bonus`,
                repriseMobileBandeau: animco.box.length > 0 || animco.banner.length > 0,
            },
            variables: {
                stock: selectedProduct.stockDetails?.stock?.status === STOCK_STATUS.IN_STOCK ? 'en_stock' : 'epuise',
                forfaitDetenu: isRenewal ? contract?.subscription.label : undefined,
            },
            products: [
                {
                    name: selectedProduct.name,
                    id: selectedProduct.entityId,
                    gencode: selectedProduct.gencode,
                    sku: selectedProduct.sku,
                    price: selectedProduct.price ?? 0,
                    type_id: selectedProduct.type,
                    manufacturer: selectedProduct.manufacturer,
                    ligneProduitNiv2: TAG_CATEGORIES[selectedProduct.type]!,
                    color: selectedProduct.color,
                    capacity: selectedProduct.capacity,
                },
            ],
        });
    };
}

export function sendProductTabsTracking(isRenewal: boolean, action: string) {
    sendTracking(isRenewal, {
        eCommerceStep: 'Fiche_produit',
        action: 'Clic_selection',
        category: 'Onglets_Fiche_produit',
        name: formatTagText(action),
    });
}

export function sendProductPortabilityTracking(isRenewal: boolean, action: string) {
    sendTracking(isRenewal, {
        eCommerceStep: 'Fiche_produit',
        action: 'Clic_selection',
        category: 'Infos_telephone_numero',
        name: formatTagText(action),
    });
}

export function sendProductConvergenceTracking(isRenewal: boolean, action: string) {
    sendTracking(isRenewal, {
        eCommerceStep: 'Fiche_produit',
        action: 'Clic_selection',
        category: 'Infos_convergence',
        name: formatTagText(action),
    });
}

export function sendProductPhoneFundingTracking(isRenewal: boolean, action: string) {
    sendTracking(isRenewal, {
        eCommerceStep: 'Fiche_produit',
        action: 'Clic_selection',
        category: 'Infos_telephone_etalement_paiement',
        name: formatTagText(action),
    });
}

export function sendProductPhonePriceRecapTracking(isRenewal: boolean, action: string) {
    sendTracking(isRenewal, {
        eCommerceStep: 'Fiche_produit',
        action: 'Clic_affichage',
        category: 'Recap_prix_telephone',
        name: formatTagText(action),
    });
}

export function sendProductPlanPriceRecapTracking(isRenewal: boolean, action: string) {
    sendTracking(isRenewal, {
        eCommerceStep: 'Fiche_produit',
        action: 'Clic_affichage',
        category: 'Recap_prix_forfait',
        name: formatTagText(action),
    });
}

export function sendProductPreviewTracking(isRenewal: boolean) {
    sendTracking(isRenewal, {
        eCommerceStep: 'Fiche_produit',
        action: 'Clic_affichage',
        category: 'Barre_visuels',
        name: 'icone_visuels',
    });
}

export function sendProductStockTracking(isRenewal: boolean, action: string) {
    sendTracking(isRenewal, {
        eCommerceStep: 'Fiche_produit',
        action: 'Clic_affichage',
        category: 'Stock_disponibilite',
        name: formatTagText(action),
    });
}

export function sendSmartChangeTracking(isRenewal: boolean, isSmartChangeSelected: boolean) {
    sendTracking(isRenewal, {
        eCommerceStep: 'Fiche_produit',
        action: 'Clic_selection',
        category: 'Choix_paiement_du_telephone',
        name: `${isSmartChangeSelected ? 'Avec' : 'Sans'}_smart_change`,
    });
}

export function sendProductCTATracking(isRenewal: boolean, name: string, source: CTASource) {
    sendTracking(isRenewal, {
        eCommerceStep: 'Fiche_produit',
        action: 'Clic_ajout_panier',
        category: 'Bouton_CTA',
        name: formatTagText(name),
        variant: {
            source: source,
        },
    });
}

export function sendTradeInStep1Tracking(isRenewal: boolean, isSmartChange: boolean = false) {
    sendTracking(isRenewal, {
        journey: 'Reprise_mobile',
        marketLine: 'Mobile',
        page: 'Infos_modele_mobile',
        pageCategory: 'Formulaire',
        variant: {
            repriseSmartChange: isSmartChange,
        },
    });
}

export function sendTradeInStep2Tracking(isRenewal: boolean, phoneName: string, isSmartChange: boolean = false) {
    sendTracking(isRenewal, {
        journey: 'Reprise_mobile',
        marketLine: 'Mobile',
        page: 'Infos_etat_mobile',
        pageCategory: 'Formulaire',
        variables: {
            ancienTelephone: formatTagText(phoneName),
        },
        variant: {
            repriseSmartChange: isSmartChange,
        },
    });
}

export function sendTradeInStep3Tracking(
    isRenewal: boolean,
    phoneName: string,
    estimationReprise: number,
    isSmartChange: boolean = false,
    reprisePossible?: boolean,
    creditRestantApresReprise?: number,
) {
    sendTracking(isRenewal, {
        journey: 'Reprise_mobile',
        marketLine: 'Mobile',
        page: 'Estimation_valeur_reprise',
        pageCategory: 'Formulaire',
        variables: {
            ancienTelephone: formatTagText(phoneName),
            estimationReprise: String(estimationReprise),
            ...(reprisePossible === undefined ? {} : { reprisePossible }),
            ...(creditRestantApresReprise === undefined
                ? {}
                : { creditRestantApresReprise: creditRestantApresReprise.toString() }),
        },
        variant: {
            repriseSmartChange: isSmartChange,
        },
    });
}

export function sendTradeInConfirmationTracking(
    isRenewal: boolean,
    phoneName: string,
    estimationReprise: number,
    isSmartChange: boolean = false,
    reprisePossible?: boolean,
    creditRestantApresReprise?: number,
) {
    sendTracking(isRenewal, {
        journey: 'Reprise_mobile',
        marketLine: 'Mobile',
        page: 'Confirmation_reprise_telephone',
        pageCategory: 'Confirmation',
        variables: {
            ancienTelephone: formatTagText(phoneName),
            estimationReprise: String(estimationReprise),
            ...(reprisePossible === undefined ? {} : { reprisePossible }),
            ...(creditRestantApresReprise === undefined
                ? {}
                : { creditRestantApresReprise: creditRestantApresReprise.toString() }),
        },
        variant: {
            repriseSmartChange: isSmartChange,
        },
    });
}
